"use client";

import Link from "next/link";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";

import type { MaskShapeVariant } from "@/components/illustrations/MaskShape";
import { GridContainer } from "@/components/layout/GridContainer";
import { Button } from "@/components/ui/button";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { MultilinkStoryblok } from "@/component-types-sb";

const shapeSvgPaths: Record<MaskShapeVariant, string> = {
  four_vertical_even:
    '<path d="M145.324 200C145.324 310.457 112.792 400 72.6619 400C32.5318 400 0 310.457 0 200C0 89.543 32.5318 0 72.6619 0C112.792 0 145.324 89.543 145.324 200Z" fill="white"/><path d="M230.216 200C230.216 310.457 197.684 400 157.554 400C117.424 400 84.8921 310.457 84.8921 200C84.8921 89.543 117.424 0 157.554 0C197.684 0 230.216 89.543 230.216 200Z" fill="white"/><path d="M315.108 200C315.108 310.457 282.576 400 242.446 400C202.316 400 169.784 310.457 169.784 200C169.784 89.543 202.316 0 242.446 0C282.576 0 315.108 89.543 315.108 200Z" fill="white"/><path d="M400 200C400 310.457 367.468 400 327.338 400C287.208 400 254.676 310.457 254.676 200C254.676 89.543 287.208 0 327.338 0C367.468 0 400 89.543 400 200Z" fill="white"/>',
  two_vertical_uneven:
    '<path d="M160 200C160 310.457 124.183 400 80 400C35.8172 400 0 310.457 0 200C0 89.5431 35.8172 0 80 0C124.183 0 160 89.5431 160 200Z" fill="white"/><path d="M400 200C400 310.457 335.081 400 255 400C174.919 400 110 310.457 110 200C110 89.5431 174.919 0 255 0C335.081 0 400 89.5431 400 200Z" fill="white"/>',
  wave: '<path d="M167.442 200C167.442 89.54 200.763 0 241.86 0C282.958 0 316.279 89.54 316.279 200H167.442Z" fill="white"/><path d="M0 200C0 89.54 33.3209 0 74.4186 0C115.516 0 148.837 89.54 148.837 200H0Z" fill="white"/><path d="M400 200C400 310.46 366.679 400 325.581 400C284.484 400 251.163 310.46 251.163 200H400Z" fill="white"/><path d="M232.558 200C232.558 310.46 199.237 400 158.14 400C117.042 400 83.7209 310.46 83.7209 200H232.558Z" fill="white"/>',
  split_half_circle:
    '<path d="M0 400C110.717 400 200.471 310.457 200.471 200C200.471 89.5431 110.717 0 0 0V400Z" fill="white"/><path d="M200.471 400C310.668 400 400 310.457 400 200C400 89.5431 310.668 0 200.471 0V400Z" fill="white"/>',
  three_horizontal_even:
    '<path d="M400 90C400 139.706 310.457 180 200 180C89.543 180 0 139.706 0 90C0 40.2944 89.543 0 200 0C310.457 0 400 40.2944 400 90Z" fill="white"/><path d="M400 200C400 249.706 310.457 290 200 290C89.543 290 0 249.706 0 200C0 150.294 89.543 110 200 110C310.457 110 400 150.294 400 200Z" fill="white"/><path d="M400 310C400 359.706 310.457 400 200 400C89.543 400 0 359.706 0 310C0 260.294 89.543 220 200 220C310.457 220 400 260.294 400 310Z" fill="white"/>',
  three_vertical_uneven:
    '<path d="M160 200C160 310.457 124.183 400 80 400C35.8172 400 0 310.457 0 200C0 89.5431 35.8172 0 80 0C124.183 0 160 89.5431 160 200Z" fill="white"/><path d="M240 200C240 310.457 204.183 400 160 400C115.817 400 80 310.457 80 200C80 89.5431 115.817 0 160 0C204.183 0 240 89.5431 240 200Z" fill="white"/><path d="M400 200C400 310.457 347.617 400 283 400C218.383 400 166 310.457 166 200C166 89.5431 218.383 0 283 0C347.617 0 400 89.5431 400 200Z" fill="white"/>',
  three_vertical_uneven_mirrored:
    '<path d="M400 200C400 310.5 364.2 400 320 400C275.8 400 240 310.5 240 200C240 89.5 275.8 0 320 0C364.2 0 400 89.5 400 200Z" fill="white"/><path d="M314 200C314 310.5 278.2 400 234 400C189.8 400 154 310.5 154 200C154 89.5 189.8 0 234 0C278.2 0 314 89.5 314 200Z" fill="white"/><path d="M234 200C234 310.5 181.6 400 117 400C52.4 400 0 310.5 0 200C0 89.5 52.4 0 117 0C181.6 0 234 89.5 234 200Z" fill="white"/>',
  circle: '<circle cx="200" cy="200" r="200" fill="white"/>',
};

const getMaskSvgUrl = (variant: MaskShapeVariant): string => {
  const paths = shapeSvgPaths[variant] || shapeSvgPaths.circle;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">${paths}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

type PromoCardStoryblok = {
  headline?: string;
  title?: string;
  image?: { filename?: string; alt?: string };
  mask_shape?: MaskShapeVariant;
  button_label?: string;
  button_link?: MultilinkStoryblok;
  _uid: string;
  component: "promo_card";
  [k: string]: any;
};

type PromoCardProps = {
  blok: PromoCardStoryblok;
};

const PromoCard = ({ blok }: PromoCardProps) => {
  const shape = (blok.mask_shape || "split_half_circle") as MaskShapeVariant;

  const buttonHref = getUrlFromLinkObject(blok.button_link) || null;

  const hasButton = blok.button_label && buttonHref;

  return (
    <GridContainer
      {...storyblokEditable(blok)}
      className="!px-0 md:!px-8 py-4  "
    >
      <div className="col-span-12 md:col-span-10 md:col-start-2  lg:col-span-8 lg:col-start-3">
        {buttonHref ? (
          <Link href={buttonHref} target={blok.button_link?.target || "_self"} className="block card-rotate bg-sky-75 rounded-3xl overflow-hidden">
            {/* Headline */}
            {blok.headline && (
              <h2 className="text-headline-xl text-black text-center p-6">
                {blok.headline}
              </h2>
            )}

            {/* Image + content area */}
            <div className="relative isolate h-[280px] md:h-[320px] lg:h-[360px]">
              {/* Image masked by shape — centered, natural aspect ratio */}
              {blok.image?.filename && (
                <div
                  className="absolute top-0 right-0 bottom-0 left-0"
                  style={{
                    maskImage: getMaskSvgUrl(shape),
                    WebkitMaskImage: getMaskSvgUrl(shape),
                    maskSize: "100% auto",
                    WebkitMaskSize: "100% auto",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "center top",
                    WebkitMaskPosition: "center top",
                  }}
                >
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || blok.title || ""}
                    fill
                    sizes="(min-width: 1024px) 66vw, (min-width: 768px) 83vw, 100vw"
                    className="object-cover"
                  />
                </div>
              )}

              {/* Content on top */}
              <div className="absolute top-0 right-0 bottom-0 left-0 z-[1] flex flex-col items-center justify-center gap-4 p-6">
                {blok.title && (
                  <p className="text-4xl md:text-6xl font-semibold text-center leading-none tracking-tight text-sun-100">
                    {blok.title}
                  </p>
                )}

                {hasButton && (
                  <Button variant="sun" size="sm" asChild>
                    <span>{blok.button_label}</span>
                  </Button>
                )}
              </div>
            </div>
          </Link>
        ) : (
          <div className="bg-sky-75 rounded-3xl overflow-hidden">
            {/* Headline */}
            {blok.headline && (
              <h2 className="text-headline-xl text-black text-center p-6">
                {blok.headline}
              </h2>
            )}

            {/* Image + content area */}
            <div className="relative isolate h-[280px] md:h-[320px] lg:h-[360px]">
              {blok.image?.filename && (
                <div
                  className="absolute top-0 right-0 bottom-0 left-0"
                  style={{
                    maskImage: getMaskSvgUrl(shape),
                    WebkitMaskImage: getMaskSvgUrl(shape),
                    maskSize: "100% auto",
                    WebkitMaskSize: "100% auto",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                    maskPosition: "center top",
                    WebkitMaskPosition: "center top",
                  }}
                >
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || blok.title || ""}
                    fill
                    sizes="(min-width: 1024px) 66vw, (min-width: 768px) 83vw, 100vw"
                    className="object-cover"
                  />
                </div>
              )}

              <div className="absolute top-0 right-0 bottom-0 left-0 z-[1] flex flex-col items-center justify-center gap-4 p-6">
                {blok.title && (
                  <p className="text-4xl md:text-6xl font-semibold text-center leading-none tracking-tight text-sun-100">
                    {blok.title}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </GridContainer>
  );
};

export default PromoCard;
